import { useEffect, useState, useCallback } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { Icon } from './SvgIcons';
import TemplatePreview from './TemplatePreview';

const sumSubMarks = (sq) => {
  if (sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0) {
    return sq.subquestions.reduce((s, ssq) => s + (parseInt(ssq.marks, 10) || 0), 0);
  }
  return parseInt(sq.marks, 10) || 0;
};

const sumQuestionMarks = (q) => {
  if (q.has_subquestions && Array.isArray(q.subquestions)) {
    return q.subquestions.reduce((s, sq) => s + sumSubMarks(sq), 0);
  }
  return parseInt(q.marks, 10) || 0;
};

const totalsFor = (questions = []) => {
  const totalMarks = questions.reduce((s, q) => s + sumQuestionMarks(q), 0);
  const totalSubs = questions.reduce(
    (s, q) => s + (q.has_subquestions && Array.isArray(q.subquestions) ? q.subquestions.length : 0),
    0,
  );
  return { totalMarks, totalSubs, totalQuestions: questions.length };
};

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const MyTemplatesList = () => {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      // College-level templates, restricted to those created by the current user.
      const { data } = await API.get('/templates?page=1&limit=10&mine=1');
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!activeTemplate) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActiveTemplate(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeTemplate]);

  const handleView = async (templateId) => {
    try {
      setLoadingDetail(true);
      setActiveTemplate({ template_id: templateId, loading: true });
      const { data } = await API.get(`/templates/${templateId}`);
      setActiveTemplate(data.template || null);
    } catch (error) {
      console.error('Error loading template:', error);
      showToast('Failed to load template details', 'error');
      setActiveTemplate(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const filtered = templates.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.template_name || '').toLowerCase().includes(q)
      || (t.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Icon name="FileText" size={20} />
          <span>My Templates ({filtered.length})</span>
        </h3>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <Icon name="Search" size={16} />
          <input
            type="text"
            className="form-input"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: '220px' }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <span className="spinner" /> Loading templates...
        </div>
      ) : filtered.length === 0 ? (
        <div className="tpl-preview-empty">
          <Icon name="FileText" size={32} />
          <p style={{ marginTop: '0.75rem' }}>
            {search.trim() ? 'No templates match your search.' : 'No templates created yet. Use the Create tab to add one.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map((t) => {
            const { totalQuestions, totalMarks, totalSubs } = totalsFor(t.questions || []);
            return (
              <div
                key={t.template_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  boxShadow: 'var(--shadow-sm)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <Icon name="Clipboard" size={16} />
                    <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.template_name || 'Untitled Template'}
                    </h4>
                  </div>
                  {t.description && (
                    <p style={{
                      margin: 0,
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {t.description}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  <span><strong style={{ color: 'var(--text-primary)' }}>{totalQuestions}</strong> Qs</span>
                  {totalSubs > 0 && <span><strong style={{ color: 'var(--text-primary)' }}>{totalSubs}</strong> Sub-Qs</span>}
                  <span><strong style={{ color: 'var(--success)' }}>{totalMarks}</strong> Marks</span>
                  {t.created_at && (
                    <span style={{ color: 'var(--text-tertiary, var(--text-secondary))' }}>
                      {formatDate(t.created_at)}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                  onClick={() => handleView(t.template_id)}
                  disabled={loadingDetail}
                >
                  <Icon name="Eye" size={14} />
                  <span>View</span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {activeTemplate && (
        <div
          onClick={() => setActiveTemplate(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem 1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              width: 'min(880px, 100%)',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'var(--shadow-xl)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Icon name="Eye" size={18} />
                <span>Template Preview</span>
              </h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                onClick={() => setActiveTemplate(null)}
                aria-label="Close preview"
              >
                <Icon name="X" size={14} />
                <span>Close</span>
              </button>
            </div>

            <div style={{ overflow: 'auto', flex: 1 }}>
              {activeTemplate.loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <span className="spinner" /> Loading template...
                </div>
              ) : (
                <TemplatePreview
                  templateName={activeTemplate.template_name}
                  description={activeTemplate.description}
                  questions={activeTemplate.questions || []}
                  difficultyWeightage={activeTemplate.difficulty_weightage}
                  classWeightage={activeTemplate.class_weightage}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTemplatesList;
